import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Briefcase, AlertTriangle, Lock, LockOpen } from "lucide-react";
import { toast } from "sonner";
import { maskCommessaCodice, COMMESSA_CODICE_RE, commessaEsempio, setCommessaFormat } from "@/lib/commessaCodice";
import { useAuth } from "@/contexts/AuthContext";
import ClienteCombobox from "@/components/ClienteCombobox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const empty = { codice: "", descrizione: "", cliente: "", attiva: true, budget_input: "", note_obbligatorie: "" };

// Iter 53: data ISO → gg/mm/aa
const fmtDataIt = (iso) =>
  iso && iso.length >= 10 ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(2, 4)}` : "";

// Iter 54: formato € italiano senza decimali
const fmtEuro = (v) =>
  (v || 0).toLocaleString("it-IT", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  });

export default function AnagraficaCommesse() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  // Iter 53: budget ore impostabile da admin e da chi inserisce rapportini
  const canBudget = !!user && (user.role === "admin" || !!user.can_edit_budget); // Iter 131: solo admin o flag in Utenti
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState("");
  // Iter 57: ordinamento lista (codice | data creazione ↓/↑)
  // Iter 65: default = più recenti prima
  const [sortBy, setSortBy] = useState("created_desc");
  // Iter 53: ore totali + ultima data per commessa
  const [oreSummary, setOreSummary] = useState({});
  // Iter 53: dialog budget rapido dalla riga
  const [budgetEdit, setBudgetEdit] = useState(null); // {id, codice, value}
  const [budgetBusy, setBudgetBusy] = useState(false);
  // Iter 54: tariffa oraria manodopera (da Impostazioni, solo admin la vede)
  const [tariffa, setTariffa] = useState(null);
  // Iter 67: dialog chiusura commessa {id, codice, date}
  const [chiusuraEdit, setChiusuraEdit] = useState(null);
  const [chiusuraBusy, setChiusuraBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/commesse");
      setItems(data || []);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Caricamento commesse non riuscito";
      toast.error(typeof msg === "string" ? msg : "Caricamento commesse non riuscito");
      setItems([]);
    }
    api
      .get("/commesse/ore-summary")
      .then((r) => setOreSummary(r.data || {}))
      .catch(() => {});
  };

  useEffect(() => { load(); api.get("/commesse/formato").then(({ data }) => setCommessaFormat(data)).catch(() => {}); }, []);

  // Iter 54: tariffa oraria da Impostazioni (solo admin)
  useEffect(() => {
    if (user?.role !== "admin") return;
    api
      .get("/settings")
      .then((r) => setTariffa(r.data?.costo_orario_default ?? 20))
      .catch(() => {});
  }, [user?.role]);

  // Iter 53: salva budget dal dialog rapido
  const saveBudget = async () => {
    if (!budgetEdit) return;
    const raw = String(budgetEdit.value ?? "").replace(",", ".").trim();
    const num = raw === "" ? null : Number(raw);
    if (raw !== "" && (!Number.isFinite(num) || num < 0)) {
      toast.error("Inserisci un numero di ore valido (es. 120)");
      return;
    }
    setBudgetBusy(true);
    try {
      await api.put(`/commesse/${budgetEdit.id}/budget-ore`, {
        budget_ore: num,
      });
      toast.success(
        num ? `Budget di ${num} h salvato su ${budgetEdit.codice}` : "Budget rimosso"
      );
      setBudgetEdit(null);
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Salvataggio budget non riuscito";
      toast.error(typeof msg === "string" ? msg : "Salvataggio budget non riuscito");
    } finally {
      setBudgetBusy(false);
    }
  };

  // Iter 67: chiudi commessa (famiglia madre + figlia -OFF) da una data
  const saveChiusura = async () => {
    if (!chiusuraEdit) return;
    if (!(chiusuraEdit.date || "").trim()) {
      toast.error("Seleziona la data di chiusura");
      return;
    }
    setChiusuraBusy(true);
    try {
      const { data } = await api.put(`/commesse/${chiusuraEdit.id}/chiusura`, {
        chiusa: true,
        data_chiusura: chiusuraEdit.date,
      });
      toast.success(
        `Commessa ${chiusuraEdit.codice} chiusa dal ${fmtDataIt(chiusuraEdit.date)}${
          (data?.ids || []).length > 1 ? " (madre e figlia -OFF insieme)" : ""
        }`
      );
      setChiusuraEdit(null);
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Chiusura non riuscita";
      toast.error(typeof msg === "string" ? msg : "Chiusura non riuscita");
    } finally {
      setChiusuraBusy(false);
    }
  };

  const riapriCommessa = async (it) => {
    if (
      !window.confirm(
        `Riaprire la commessa ${it.codice}? Sarà di nuovo possibile inserire ore nei rapportini (anche la commessa collegata madre/-OFF verrà riaperta).`
      )
    )
      return;
    try {
      await api.put(`/commesse/${it.id}/chiusura`, { chiusa: false });
      toast.success(`Commessa ${it.codice} riaperta`);
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Riapertura non riuscita";
      toast.error(typeof msg === "string" ? msg : "Riapertura non riuscita");
    }
  };


  // Iter 30z: alert "commesse senza cliente validato" all'ingresso pagina
  const [noCliCommesse, setNoCliCommesse] = useState([]);
  const [clientiValidi, setClientiValidi] = useState([]);
  const [noCliOpen, setNoCliOpen] = useState(false);
  const [noCliAssign, setNoCliAssign] = useState({});
  const [noCliBusy, setNoCliBusy] = useState(false);

  useEffect(() => {
    if (!isAdmin) return;
    const flag = "commesse_no_cliente_page_alert_v1";
    if (sessionStorage.getItem(flag)) return;
    (async () => {
      try {
        const { data } = await api.get("/admin/commesse-no-cliente");
        const cms = data?.commesse || [];
        if (cms.length > 0) {
          setNoCliCommesse(cms);
          setClientiValidi(data?.clienti_validi || []);
          setNoCliOpen(true);
        }
        sessionStorage.setItem(flag, "1");
      } catch (err) {
        console.warn("[commesse] check senza-cliente fallito:", err?.message);
      }
    })();
  }, [isAdmin]);

  const applyNoCliFixes = async () => {
    const entries = Object.entries(noCliAssign).filter(
      ([, v]) => v && v.trim()
    );
    if (entries.length === 0) {
      setNoCliOpen(false);
      return;
    }
    setNoCliBusy(true);
    let updated = 0;
    let failed = 0;
    for (const [cid, newCliente] of entries) {
      const cm = noCliCommesse.find((c) => c.id === cid);
      if (!cm) { failed += 1; continue; }
      try {
        await api.put(`/commesse/${cid}`, { ...cm, cliente: newCliente });
        updated += 1;
      } catch (_) {
        failed += 1;
      }
    }
    if (updated > 0) {
      toast.success(`Aggiornate ${updated} commesse con cliente.`);
      load();
    }
    if (failed > 0) toast.error(`Impossibile aggiornare ${failed} commesse.`);
    setNoCliOpen(false);
    setNoCliBusy(false);
    setNoCliAssign({});
  };

  const filtered = items
    .filter((it) => {
      const q = search.toLowerCase();
      return (
        it.codice.toLowerCase().includes(q) ||
        (it.descrizione || "").toLowerCase().includes(q) ||
        (it.cliente || "").toLowerCase().includes(q)
      );
    })
    .sort((a, b) => {
      // Iter 57: ordinamento per codice (default) o per data di creazione
      if (sortBy === "created_desc" || sortBy === "created_asc") {
        const da = a.created_at || "";
        const db_ = b.created_at || "";
        // senza data → in fondo
        if (!da && !db_) return (a.codice || "").localeCompare(b.codice || "");
        if (!da) return 1;
        if (!db_) return -1;
        return sortBy === "created_desc"
          ? db_.localeCompare(da)
          : da.localeCompare(db_);
      }
      return (a.codice || "").localeCompare(b.codice || "");
    });

  const startCreate = () => { setForm(empty); setEditingId(null); setOpen(true); setFamilyHint(null); };
  const startEdit = (it) => {
    setForm({
      ...it,
      budget_input: it.budget_ore != null ? String(it.budget_ore) : "",
    });
    setEditingId(it.id);
    setOpen(true);
    setFamilyHint(null);
  };

  // Iter 31a: quando l'utente digita il codice "EG-XX-...", interroga l'API
  // famiglia per suggerire il cliente già usato dalle sorelle EG-XX-*.
  const [familyHint, setFamilyHint] = useState(null);
  useEffect(() => {
    const cod = (form.codice || "").trim();
    if (!cod || cod.length < 5 || !open) { setFamilyHint(null); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(
          `/commesse/famiglia/cliente?codice=${encodeURIComponent(cod)}`
        );
        if (cancelled) return;
        if (data?.cliente && data?.count > 0) {
          // escludi se stesso in modifica
          const altri = (data.commesse || []).filter((c) => c.id !== editingId);
          setFamilyHint({ ...data, commesse: altri, count: altri.length });
          // auto-applica solo se il cliente è ancora vuoto e siamo in creazione
          if (!editingId && !(form.cliente || "").trim()) {
            setForm((f) => ({ ...f, cliente: data.cliente }));
          }
        } else {
          setFamilyHint(null);
        }
      } catch (err) {
        console.warn("[commesse] family hint fallito:", err?.message);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.codice, open, editingId]);

  const save = async () => {
    if (!form.codice.trim()) {
      toast.error("Il codice commessa è obbligatorio");
      return;
    }
    // Iter 50/55: formato configurato obbligatorio per TUTTI.
    // Eccezione: modifica di una commessa legacy SENZA cambiare il codice.
    const originale = editingId ? items.find((it) => it.id === editingId) : null;
    if (
      !COMMESSA_CODICE_RE.test(form.codice.trim()) &&
      !(originale && form.codice.trim() === (originale.codice || ""))
    ) {
      toast.error(
        `Formato codice non valido: usare ${commessaEsempio()} o ${commessaEsempio()}-OFF. Il formato è obbligatorio per tutti.`
      );
      return;
    }
    if (!(form.descrizione || "").trim()) {
      toast.error(
        "Descrizione del lavoro obbligatoria: indica in breve di che lavoro si tratta."
      );
      return;
    }
    if (!(form.cliente || "").trim()) {
      toast.error(
        "Cliente obbligatorio: seleziona dall'anagrafica o crealo al volo dal form."
      );
      return;
    }
    try {
      // Iter 53: budget ore facoltativo (endpoint dedicato, traccia chi lo imposta)
      const rawBudget = String(form.budget_input ?? "").replace(",", ".").trim();
      const budgetNum = rawBudget === "" ? null : Number(rawBudget);
      if (rawBudget !== "" && (!Number.isFinite(budgetNum) || budgetNum < 0)) {
        toast.error("Budget ore non valido: inserisci un numero (es. 120)");
        return;
      }
      let savedId = editingId;
      if (editingId) {
        await api.put(`/commesse/${editingId}`, form);
      } else {
        const { data: created } = await api.post("/commesse", form);
        savedId = created?.id;
      }
      const prevBudget = originale?.budget_ore ?? null;
      if (savedId && budgetNum !== prevBudget) {
        try {
          await api.put(`/commesse/${savedId}/budget-ore`, { budget_ore: budgetNum });
        } catch (err) {
          console.warn("[commesse] salvataggio budget fallito:", err?.message);
          toast.error("Commessa salvata ma budget ore NON salvato");
        }
      }
      // Iter 31a: dopo il save, controlla quante sorelle della famiglia
      // sono state sincronizzate col nuovo cliente, e segnalalo all'utente.
      let syncMsg = "";
      try {
        const { data: famAfter } = await api.get(
          `/commesse/famiglia/cliente?codice=${encodeURIComponent(form.codice)}`
        );
        const altri = (famAfter?.commesse || []).filter(
          (c) => c.id !== editingId
        );
        const allineati = altri.filter(
          (c) => (c.cliente || "").trim() === (form.cliente || "").trim()
        ).length;
        if (allineati > 0) {
          syncMsg = ` · Cliente sincronizzato su ${allineati} altr${allineati === 1 ? "a" : "e"} commess${allineati === 1 ? "a" : "e"} della famiglia ${famAfter.prefix}`;
        }
      } catch (err) {
        console.warn("[commesse] sync famiglia fallito:", err?.message);
      }
      toast.success("Commessa salvata" + syncMsg);
      setOpen(false);
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Salvataggio non riuscito";
      toast.error(typeof msg === "string" ? msg : "Salvataggio non riuscito");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Spostare la commessa nel cestino?")) return;
    try {
      await api.delete(`/commesse/${id}`);
      toast.success("Spostata nel cestino (recuperabile per 30 giorni)");
      load();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || "";
      if (status === 409) {
        if (window.confirm(
          `${detail}\n\nVuoi spostare comunque la commessa nel cestino E rimuovere le righe dei rapportini collegati?\nLa commessa resterà recuperabile per 30 giorni, ma le righe nei rapportini saranno perse.`
        )) {
          try {
            const res = await api.delete(`/commesse/${id}?cascade=true`);
            toast.success(
              `Commessa nel cestino. Aggiornati ${res.data?.rapportini_modified || 0} rapportini.`
            );
            load();
          } catch (e2) {
            console.error("[commesse] cascade delete failed:", e2);
            toast.error("Eliminazione (cascade) non riuscita");
          }
        }
      } else {
        console.error("[commesse] delete failed:", err);
        toast.error("Eliminazione non riuscita");
      }
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-8 py-6 space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
            Anagrafica
          </div>
          <h1 className="font-heading font-black text-3xl md:text-4xl tracking-tight">
            Commesse e budget
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {items.length} commesse registrate. Codici univoci · budget ore e
            ore lavorate a colpo d&apos;occhio.
          </p>
        </div>
        {isAdmin && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                onClick={startCreate}
                data-testid="add-commessa-button"
                className="h-12 bg-[#0044FF] hover:bg-[#0033CC]"
              >
                <Plus className="h-5 w-5 mr-2" /> Nuova commessa
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {editingId ? "Modifica commessa" : "Nuova commessa"}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label className="font-bold">Codice *</Label>
                  <Input
                    value={form.codice}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        codice: maskCommessaCodice(e.target.value, {
                          deleting: e.target.value.length < (form.codice || "").length,
                        }).toUpperCase(),
                      })
                    }
                    placeholder={`Es: ${commessaEsempio().replace(/X/g, "9")}`}
                    className="h-12 mt-1 font-mono uppercase"
                    data-testid="commessa-codice-input"
                  />
                  {familyHint && familyHint.count > 0 && (
                    <div
                      className="mt-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900"
                      data-testid="commessa-family-hint"
                    >
                      <div className="font-semibold">
                        Famiglia {familyHint.prefix} · {familyHint.count} commess{familyHint.count === 1 ? "a" : "e"} collegat{familyHint.count === 1 ? "a" : "e"}
                      </div>
                      <div className="opacity-80">
                        Cliente sincronizzato:{" "}
                        <span className="font-bold">{familyHint.cliente || "—"}</span>
                      </div>
                      <div className="opacity-70 mt-0.5 truncate">
                        {(familyHint.commesse || [])
                          .slice(0, 5)
                          .map((c) => c.codice)
                          .join(" · ")}
                        {familyHint.count > 5 ? " · …" : ""}
                      </div>
                    </div>
                  )}
                </div>
                <div>
                  <Label className="font-bold">
                    Descrizione <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    value={form.descrizione}
                    onChange={(e) =>
                      setForm({ ...form, descrizione: e.target.value })
                    }
                    placeholder="Breve descrizione del lavoro/cantiere (obbligatoria)"
                    className="h-12 mt-1"
                    data-testid="commessa-descrizione-input"
                  />
                </div>
                <div>
                  <Label className="font-bold">
                    Cliente <span className="text-red-500">*</span>
                  </Label>
                  <ClienteCombobox
                    testId="commessa-cliente-input"
                    value={form.cliente}
                    onChange={(v) => setForm({ ...form, cliente: v })}
                    defaultStabilimento={
                      (form.codice.match(/^[A-Z]+-(\d+)/i) || [])[1] || ""
                    }
                  />
                </div>
                <div className="flex items-center justify-between border rounded-md p-3">
                  <div>
                    <div className="font-bold text-sm">Attiva</div>
                    <div className="text-xs text-slate-500">
                      Le commesse non attive non vengono suggerite nei nuovi rapportini
                    </div>
                  </div>
                  <Switch
                    checked={form.attiva}
                    onCheckedChange={(v) => setForm({ ...form, attiva: v })}
                    data-testid="commessa-attiva-switch"
                  />
                </div>
                {user?.role === "admin" && (
                  <div className="rounded-md border border-orange-200 bg-orange-50 p-3">
                    <Label className="font-bold text-orange-900">
                      Note obbligatorie nei rapportini
                    </Label>
                    <p className="text-[11px] text-orange-800 mt-0.5 mb-2">
                      Se attivo, gli operai NON possono salvare ore su questa
                      commessa senza una nota di almeno 10 caratteri.
                    </p>
                    <Select
                      value={form.note_obbligatorie || "no"}
                      onValueChange={(v) =>
                        setForm({ ...form, note_obbligatorie: v === "no" ? "" : v })
                      }
                    >
                      <SelectTrigger
                        className="h-11 bg-white"
                        data-testid="commessa-note-obbligatorie-select"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="no">No, note facoltative</SelectItem>
                        <SelectItem value="madre">Solo commessa madre</SelectItem>
                        <SelectItem value="figlia">Solo commessa figlia (-OFF)</SelectItem>
                        <SelectItem value="entrambe">Madre e figlia (entrambe)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <Label className="font-bold">Budget ore (facoltativo)</Label>
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={form.budget_input || ""}
                    onChange={(e) =>
                      setForm({ ...form, budget_input: e.target.value })
                    }
                    placeholder="es. 120 — vuoto = nessun budget"
                    className="h-12 mt-1"
                    data-testid="commessa-budget-input"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">
                    Verrà registrato chi lo ha inserito ({user?.name || "tu"}).
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>
                  Annulla
                </Button>
                <Button
                  onClick={save}
                  data-testid="save-commessa-button"
                  className="bg-[#0044FF] hover:bg-[#0033CC]"
                >
                  Salva
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Card className="p-4 flex flex-col sm:flex-row gap-3">
        <Input
          placeholder="Cerca commessa, cliente…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-12 flex-1"
          data-testid="search-commesse-input"
        />
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger
            className="h-12 w-full sm:w-64"
            data-testid="sort-commesse-select"
          >
            <SelectValue placeholder="Ordina per…" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="codice">Codice (A→Z)</SelectItem>
            <SelectItem value="created_desc">
              Data creazione ↓ (più recenti)
            </SelectItem>
            <SelectItem value="created_asc">
              Data creazione ↑ (più vecchie)
            </SelectItem>
          </SelectContent>
        </Select>
      </Card>

      {filtered.length === 0 ? (
        <Card className="p-10 text-center">
          <Briefcase className="h-10 w-10 mx-auto text-slate-300" />
          <div className="font-heading font-bold text-lg mt-3">Nessuna commessa</div>
          <p className="text-sm text-slate-500">
            Crea la prima commessa per iniziare.
          </p>
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map((it) => {
            const oreInfo = oreSummary[(it.codice || "").toUpperCase()];
            const oreTot = oreInfo?.ore_tot || 0;
            const overBudget = it.budget_ore != null && oreTot > it.budget_ore;
            const pct =
              it.budget_ore > 0
                ? Math.round((oreTot / it.budget_ore) * 100)
                : null;
            return (
            <Card
              key={it.id}
              className="p-4 flex flex-wrap items-center gap-3"
              data-testid={`commessa-row-${it.id}`}
            >
              <div className="font-mono font-bold text-base bg-slate-900 text-white px-3 py-2 rounded-md">
                {it.codice}
              </div>
              <div className="flex-1 min-w-[180px]">
                <div className="font-heading font-bold truncate">
                  {it.descrizione || "—"}
                </div>
                <div className="text-xs text-slate-500 truncate">
                  {it.cliente ? `Cliente: ${it.cliente} · ` : ""}
                  {it.attiva ? "Attiva" : "Disattivata"}
                  {it.created_at && (
                    <span
                      className="text-slate-400"
                      data-testid={`created-info-${it.id}`}
                    >
                      {" "}· creata {fmtDataIt(it.created_at.slice(0, 10))}
                      {it.created_by_name ? ` (${it.created_by_name})` : ""}
                    </span>
                  )}
                  {it.pending_confirmation && (
                    <span
                      className="ml-2 inline-flex items-center gap-1 text-amber-700 font-bold"
                      data-testid={`pending-commessa-badge-${it.id}`}
                      title={`Creata da ${it.created_by_name || "un utente"} — in attesa di conferma admin`}
                    >
                      · ⏳ da confermare
                    </span>
                  )}
                  {it.chiusa && it.chiusa_dal && (
                    <span
                      className="ml-2 inline-flex items-center gap-1 text-red-700 font-bold"
                      data-testid={`chiusa-badge-${it.id}`}
                      title={`Chiusa da ${it.chiusa_da_nome || "—"}${
                        it.chiusa_il ? ` il ${fmtDataIt(it.chiusa_il.slice(0, 10))}` : ""
                      } — dal ${fmtDataIt(it.chiusa_dal)} non si possono più inserire ore`}
                    >
                      · 🔒 CHIUSA dal {fmtDataIt(it.chiusa_dal)}
                    </span>
                  )}
                </div>
              </div>
              {/* Iter 53: ore lavorate totali + ultima data con ore */}
              <div
                className="text-right shrink-0 min-w-[92px]"
                data-testid={`ore-info-${it.id}`}
              >
                <div className="text-[10px] uppercase tracking-wider text-slate-400">
                  Ore lavorate
                </div>
                <div
                  className={`font-mono font-bold text-sm ${
                    overBudget ? "text-red-600" : ""
                  }`}
                  title={overBudget ? "Budget ore superato!" : undefined}
                >
                  {oreTot} h
                </div>
                <div className="text-[10px] text-slate-500">
                  {oreInfo?.ultima_data
                    ? `ultime: ${fmtDataIt(oreInfo.ultima_data)}`
                    : "nessuna ora"}
                </div>
                {tariffa != null && oreTot > 0 && (
                  <div
                    className="text-[10px] font-semibold text-emerald-700"
                    title={`Costo manodopera all'azienda: ${oreTot} h × ${tariffa} €/h (tariffa in Impostazioni). NON include materiali e altri costi.`}
                    data-testid={`costo-manodopera-${it.id}`}
                  >
                    manodopera: {fmtEuro(oreTot * tariffa)}
                  </div>
                )}
              </div>
              {/* Iter 53: budget ore (con chi l'ha inserito) — click per modificare */}
              <button
                type="button"
                disabled={!canBudget}
                onClick={() =>
                  canBudget &&
                  setBudgetEdit({
                    id: it.id,
                    codice: it.codice,
                    value: it.budget_ore != null ? String(it.budget_ore) : "",
                  })
                }
                data-testid={`budget-btn-${it.id}`}
                title={
                  canBudget
                    ? "Clicca per impostare/modificare il budget ore"
                    : "Budget ore (sola lettura)"
                }
                className={`text-right shrink-0 min-w-[92px] rounded-md px-2 py-1 border transition-colors ${
                  it.budget_ore != null
                    ? "border-blue-200 bg-blue-50/60"
                    : "border-dashed border-slate-300"
                } ${canBudget ? "hover:border-[#0044FF] cursor-pointer" : "cursor-default"}`}
              >
                {it.budget_ore != null ? (
                  <>
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">
                      Budget ore
                    </div>
                    <div className="font-mono font-bold text-sm text-[#0044FF]">
                      {it.budget_ore} h
                    </div>
                    <div
                      className="text-[10px] text-slate-500 truncate max-w-[130px]"
                      title={`Inserito da ${it.budget_ore_da_nome || "—"}${
                        it.budget_ore_il
                          ? ` il ${fmtDataIt(it.budget_ore_il.slice(0, 10))}`
                          : ""
                      }`}
                    >
                      ({it.budget_ore_da_nome || "—"})
                    </div>
                  </>
                ) : (
                  <span className="text-xs font-semibold text-slate-500">
                    + Budget
                  </span>
                )}
              </button>
              {isAdmin && (
                <div className="flex gap-2">
                  {it.chiusa ? (
                    <Button
                      size="icon"
                      variant="outline"
                      className="h-10 w-10 text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                      onClick={() => riapriCommessa(it)}
                      data-testid={`riapri-commessa-${it.id}`}
                      title={`Riapri la commessa (chiusa dal ${fmtDataIt(it.chiusa_dal || "")})`}
                    >
                      <LockOpen className="h-4 w-4" />
                    </Button>
                  ) : (
                    <Button
                      size="icon"
                      variant="outline"
                      className="h-10 w-10 text-amber-700 border-amber-200 hover:bg-amber-50"
                      onClick={() =>
                        setChiusuraEdit({
                          id: it.id,
                          codice: it.codice,
                          date: new Date().toISOString().slice(0, 10),
                        })
                      }
                      data-testid={`chiudi-commessa-${it.id}`}
                      title="Chiudi la commessa da una data: da quella data in poi niente più ore nei rapportini"
                    >
                      <Lock className="h-4 w-4" />
                    </Button>
                  )}
                  <Button
                    size="icon"
                    variant="outline"
                    className="h-10 w-10"
                    onClick={() => startEdit(it)}
                    data-testid={`edit-commessa-${it.id}`}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    size="icon"
                    variant="outline"
                    className="h-10 w-10 text-red-600 border-red-200 hover:bg-red-50"
                    onClick={() => remove(it.id)}
                    data-testid={`delete-commessa-${it.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              )}
              {/* Iter 53b: barra avanzamento budget */}
              {pct !== null && (
                <div className="basis-full" data-testid={`budget-bar-${it.id}`}>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${
                          pct > 100
                            ? "bg-red-500"
                            : pct >= 80
                            ? "bg-amber-500"
                            : "bg-emerald-500"
                        }`}
                        style={{ width: `${Math.min(pct, 100)}%` }}
                      />
                    </div>
                    <div
                      className={`text-[11px] font-mono font-bold shrink-0 ${
                        pct > 100
                          ? "text-red-600"
                          : pct >= 80
                          ? "text-amber-600"
                          : "text-slate-500"
                      }`}
                    >
                      {oreTot}/{it.budget_ore} h · {pct}%
                    </div>
                  </div>
                </div>
              )}
            </Card>
            );
          })}
        </div>
      )}

      {/* Iter 53: dialog budget ore rapido dalla riga */}
      <Dialog open={!!budgetEdit} onOpenChange={(o) => !o && setBudgetEdit(null)}>
        <DialogContent className="max-w-sm" data-testid="budget-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              Budget ore — <span className="font-mono">{budgetEdit?.codice}</span>
            </DialogTitle>
          </DialogHeader>
          <div>
            <Label className="font-bold">Ore a budget</Label>
            <Input
              type="text"
              inputMode="decimal"
              value={budgetEdit?.value || ""}
              onChange={(e) =>
                setBudgetEdit((prev) => ({ ...prev, value: e.target.value }))
              }
              placeholder="es. 120 — vuoto = rimuovi budget"
              className="h-12 mt-1"
              autoFocus
              data-testid="budget-dialog-input"
            />
            <p className="text-[11px] text-slate-500 mt-1.5">
              Verrà registrato che il budget è stato inserito da{" "}
              <strong>{user?.name || user?.email}</strong>.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setBudgetEdit(null)}
              disabled={budgetBusy}
              data-testid="budget-dialog-cancel"
            >
              Annulla
            </Button>
            <Button
              onClick={saveBudget}
              disabled={budgetBusy}
              className="bg-[#0044FF] hover:bg-[#0033CC]"
              data-testid="budget-dialog-save"
            >
              {budgetBusy ? "Salvataggio…" : "Salva budget"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter 67: dialog chiusura commessa */}
      <Dialog open={!!chiusuraEdit} onOpenChange={(o) => !o && setChiusuraEdit(null)}>
        <DialogContent className="max-w-sm" data-testid="chiusura-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Lock className="h-5 w-5 text-amber-600" />
              Chiudi commessa — <span className="font-mono">{chiusuraEdit?.codice}</span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="font-bold">Chiusa a partire dal</Label>
              <Input
                type="date"
                value={chiusuraEdit?.date || ""}
                onChange={(e) =>
                  setChiusuraEdit((prev) => ({ ...prev, date: e.target.value }))
                }
                className="h-12 mt-1"
                data-testid="chiusura-dialog-date"
              />
            </div>
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
              Da questa data <b>in poi</b> non sarà più possibile inserire ore nei
              rapportini su questa commessa. Le ore con data <b>precedente</b> restano
              modificabili. Se esiste la commessa collegata (madre o figlia{" "}
              <span className="font-mono font-bold">-OFF</span>) verrà chiusa insieme.
              Potrai riaprirla in qualsiasi momento.
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setChiusuraEdit(null)}
              disabled={chiusuraBusy}
              data-testid="chiusura-dialog-cancel"
            >
              Annulla
            </Button>
            <Button
              onClick={saveChiusura}
              disabled={chiusuraBusy || !(chiusuraEdit?.date || "").trim()}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="chiusura-dialog-save"
            >
              {chiusuraBusy ? "Chiusura…" : "Chiudi commessa"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter 30z: alert "commesse senza cliente validato" all'ingresso pagina */}
      <AlertDialog open={noCliOpen} onOpenChange={(o) => !o && setNoCliOpen(false)}>
        <AlertDialogContent className="max-w-2xl" data-testid="commesse-no-cliente-alert">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              {noCliCommesse.length} commesse senza cliente
            </AlertDialogTitle>
            <AlertDialogDescription>
              Queste commesse hanno il campo <b>Cliente</b> vuoto o non collegato
              a un cliente con P.IVA. Assegna un cliente esistente a ciascuna,
              oppure chiudi e fallo singolarmente dal pulsante di modifica.
            </AlertDialogDescription>
          </AlertDialogHeader>

          {clientiValidi.length === 0 ? (
            <div className="border border-amber-300 bg-amber-50 rounded-md p-3 text-sm">
              <b>Nessun cliente con P.IVA in anagrafica.</b> Vai prima su{" "}
              <i>Anagrafica → Clienti</i>, completa almeno una scheda, poi
              torna qui.
            </div>
          ) : (
            <div className="max-h-80 overflow-y-auto border rounded-md divide-y">
              {noCliCommesse.map((cm) => (
                <div key={cm.id} className="px-3 py-2 flex flex-col gap-1.5">
                  <div className="flex-1 min-w-0">
                    <div className="font-mono font-bold text-sm">{cm.codice}</div>
                    <div className="text-xs text-slate-500 truncate">
                      {cm.descrizione || "—"} ·{" "}
                      <span className="text-amber-700">
                        attuale: {cm.cliente?.trim() ? `"${cm.cliente}"` : "vuoto"}
                      </span>
                    </div>
                  </div>
                  <select
                    value={noCliAssign[cm.id] || ""}
                    onChange={(e) =>
                      setNoCliAssign((prev) => ({ ...prev, [cm.id]: e.target.value }))
                    }
                    data-testid={`commesse-no-cliente-select-${cm.id}`}
                    className="h-9 rounded-md border border-slate-200 px-2 text-sm bg-white"
                  >
                    <option value="">— Lascia invariato —</option>
                    {clientiValidi.map((cl) => (
                      <option key={cl.id} value={cl.nome}>
                        {cl.nome} (P.IVA {cl.partita_iva})
                        {cl.citta ? ` · ${cl.citta}` : ""}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel data-testid="commesse-no-cliente-cancel">
              Annulla
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="commesse-no-cliente-save"
              className="bg-[#0044FF] hover:bg-[#0033CC] disabled:opacity-50"
              disabled={
                noCliBusy ||
                clientiValidi.length === 0 ||
                Object.values(noCliAssign).filter((v) => v && v.trim()).length === 0
              }
              onClick={applyNoCliFixes}
            >
              {noCliBusy
                ? "In corso…"
                : `Aggiorna ${
                    Object.values(noCliAssign).filter((v) => v && v.trim()).length
                  } commesse`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
